// @ts-check
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { journals } from 'services/inputTokens';
import { getFilter, getOrganisationView, SLICE } from 'selectors/organisation';
import {
  setFilterGroup,
  setNamedAuthorFilterGroup,
} from 'actions/organisation';
import {
  advancedKeys,
  advancedResearcerKeys,
} from 'components/Search/Filters/keys';
import { tagRender } from 'components/common/TagRender';
import { renderOption } from 'components/common/OptionRender';

import OrgFilterField from './OrgFilterField';
import OrgFilterSection from './OrgFilterSection';
import { applyOrgPresetSideEffects } from './orgPresetSideEffects';
import { ORGANISATIONWORKITEMVIEW } from '../helpers';

// SCIL-17610: an activity row is one record with possibly several authors, so
// "contains an email" needs explaining on the Activity tab. People needs none.
const ACTIVITY_VALID_EMAILS_HELP =
  'Shows activity where the associated person has an email. A record with several authors appears if any of them has one.';

// The named-author checkboxes live under the `namedAuthor` group in the slice
// (not as top-level keys), so the group itself must be listed for the section
// badge to count them.
const activeKeys = [
  ...Object.keys(advancedKeys),
  ...Object.keys(advancedResearcerKeys),
  'namedAuthor',
];

/**
 * Reads from the namedAuthor sub-group in the shared org filters slice.
 * @param {number} orgId
 * @param {string} id
 * @returns {function} Redux selector
 */
const getNamedAuthorFilter = (orgId, id) => state => {
  const na = state[SLICE].filters[orgId]?.namedAuthor;
  return na ? na[id] : undefined;
};

/**
 * @param {{
 *   organisationId: number,
 *   loading?: boolean,
 * }} props
 */
const OrgAdvancedSection = ({ organisationId, loading }) => {
  const dispatch = useDispatch();
  const isActivityTab =
    useSelector(getOrganisationView) === ORGANISATIONWORKITEMVIEW;

  // Mirrors main search's KOL toggle: turning KOL on seeds the publication /
  // research-impact defaults (only where unset); turning it off clears them.
  const handleKol = value => {
    dispatch(setFilterGroup(organisationId, 'kol', value));
    if (value) {
      dispatch(applyOrgPresetSideEffects(organisationId, { kol: true }));
    } else {
      dispatch(setFilterGroup(organisationId, 'threeYearHIndexAverage', 0));
      dispatch(setFilterGroup(organisationId, 'totalPublications', 0));
    }
  };

  // Editing a value the KOL preset seeded means the preset no longer holds.
  const handleKolGroup = (key, value) => {
    dispatch(setFilterGroup(organisationId, 'kol', false));
    dispatch(setFilterGroup(organisationId, key, value));
  };

  return (
    <OrgFilterSection
      organisationId={organisationId}
      icon='settings'
      title='Advanced / KOL'
      activeKeys={activeKeys}
    >
      <OrgFilterField
        filterType='switch'
        filterId='kol'
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        onChange={handleKol}
        disabled={loading}
      >
        Key Opinion Leaders Only
      </OrgFilterField>
      <OrgFilterField
        filterType='selectIncludeExclude'
        mode={'multiple'}
        style={{ width: '100%' }}
        renderOption={renderOption}
        tagRender={tagRender}
        filterId={advancedKeys.journal.key}
        placeholder='Journals or Preprint Repository'
        helpText='Filter results by journals or preprint repositories relating to your search'
        options={journals}
        minInputSize={3}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        disabled={loading}
      />
      <OrgFilterField
        filterType='number'
        filterId={advancedKeys.totalPublications.key}
        placeholder='Min. Publications'
        min={0}
        onChange={value => handleKolGroup('totalPublications', value)}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        disabled={loading}
      />
      <OrgFilterField
        filterType='number'
        filterId={advancedKeys.totalCollaboratorsOverall.key}
        placeholder='Min. Collaborators'
        min={0}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        disabled={loading}
      />
      <OrgFilterField
        filterType='number'
        filterId={advancedKeys.totalFirstNamedAuthorCount.key}
        placeholder='First Author Positions'
        min={0}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        disabled={loading}
      />
      <OrgFilterField
        filterType='number'
        filterId={advancedKeys.totalLastNamedAuthorCount.key}
        placeholder='Last Author Positions'
        min={0}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        disabled={loading}
      />
      <OrgFilterField
        filterType='number'
        filterId={advancedKeys.totalGrants.key}
        placeholder='Min. Total Funding Awards'
        min={0}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        disabled={loading}
      />
      <OrgFilterField
        filterType='number'
        filterId={advancedKeys.threeYearHIndexAverage.key}
        placeholder='Min. Research Impact (3 Year Avg)'
        min={0}
        onChange={value => handleKolGroup('threeYearHIndexAverage', value)}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        disabled={loading}
      />
      <OrgFilterField
        filterType='checkbox'
        filterId={advancedKeys.firstAuthor.key}
        labelText='First Named Author'
        organisationId={organisationId}
        setFilterGroup={setNamedAuthorFilterGroup}
        getFilter={getNamedAuthorFilter}
        disabled={loading}
      />
      <OrgFilterField
        filterType='checkbox'
        filterId={advancedKeys.middleAuthor.key}
        labelText='Middle Named Author'
        organisationId={organisationId}
        setFilterGroup={setNamedAuthorFilterGroup}
        getFilter={getNamedAuthorFilter}
        disabled={loading}
      />
      <OrgFilterField
        filterType='checkbox'
        filterId={advancedKeys.lastAuthor.key}
        labelText='Last Named Author'
        organisationId={organisationId}
        setFilterGroup={setNamedAuthorFilterGroup}
        getFilter={getNamedAuthorFilter}
        disabled={loading}
      />
      <OrgFilterField
        filterType='checkbox'
        filterId={advancedResearcerKeys.validEmails.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        disabled={loading}
        helpText={isActivityTab ? ACTIVITY_VALID_EMAILS_HELP : undefined}
        labelText={advancedResearcerKeys.validEmails.description}
      />
      <OrgFilterField
        filterType='checkbox'
        filterId={advancedResearcerKeys.excludeRecentlyExportedEmails.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        helpText={advancedResearcerKeys.excludeRecentlyExportedEmails.tooltip}
        disabled={loading}
        labelText={
          advancedResearcerKeys.excludeRecentlyExportedEmails.description
        }
      />
    </OrgFilterSection>
  );
};

OrgAdvancedSection.propTypes = {
  organisationId: PropTypes.number.isRequired,
  loading: PropTypes.bool,
};

export default OrgAdvancedSection;
